import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  LayoutDashboard, GitBranch, Scissors, Users, UserCog,
  CalendarDays, Receipt, Package, Megaphone, CircleDollarSign,
  BarChart2, Settings, LogOut, CreditCard, UserPlus, Star, Wallet, Tags, Smartphone, type LucideIcon,
} from 'lucide-react';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { logout } from '../../store/slices/authSlice';
import { toggleSidebar } from '../../store/slices/uiSlice';
import { ROUTE_MODULE, hasModuleAccess } from '../../utils/moduleAccess';

interface NavItem { path: string; label: string; Icon: LucideIcon; }

const NAV_ITEMS: NavItem[] = [
  { path: '/dashboard',     label: 'Dashboard',             Icon: LayoutDashboard },
  { path: '/calendar',      label: 'Appointments',          Icon: CalendarDays },
  { path: '/clients',       label: 'Customer Management',   Icon: Users      },
  { path: '/leads',         label: 'Leads & CRM',           Icon: UserPlus   },
  { path: '/services',      label: 'Service Catalog',       Icon: Scissors   },
  { path: '/invoices',      label: 'Billing & Invoicing',   Icon: Receipt    },
  { path: '/package-redemptions', label: 'Package Receipts', Icon: Receipt   },
  { path: '/membership',    label: 'Memberships',           Icon: CreditCard },
  { path: '/packages',      label: 'Packages',              Icon: Wallet     },
  { path: '/vouchers',      label: 'Vouchers',              Icon: Tags       },
  { path: '/loyalty',       label: 'Rewards Program',       Icon: Star       },
  { path: '/marketing',     label: 'Marketing & Campaigns', Icon: Megaphone  },
  { path: '/inventory',     label: 'Inventory & Products',  Icon: Package    },
  { path: '/expenses',      label: 'Expenses',              Icon: CircleDollarSign },
  { path: '/staff',         label: 'Staff Management',      Icon: UserCog    },
  { path: '/reports',       label: 'Reports & Analytics',   Icon: BarChart2  },
  { path: '/branches',      label: 'Branch Management',     Icon: GitBranch  },
  { path: '/settings',      label: 'Settings',              Icon: Settings   },
  { path: '/mobile-home',   label: 'Mobile Home Page',      Icon: Smartphone },
];

/* ── styled ─────────────────────────────────────────────────────────────── */
const Wrap = styled.aside<{ $open: boolean }>`
  /* Mobile: slide-in overlay */
  position: fixed; top: 0; left: 0; bottom: 0;
  width: 248px; min-width: 248px; flex-shrink: 0;
  background: #ffffff;
  border-right: 1px solid #EEEEEE;
  display: flex; flex-direction: column;
  z-index: 200;
  transform: ${p => p.$open ? 'translateX(0)' : 'translateX(-100%)'};
  transition: transform 0.22s ease;

  /* Desktop: in-flow sticky sidebar */
  @media (min-width: 1024px) {
    position: sticky;
    top: 0;
    height: 100vh;
    overflow-y: auto;
    align-self: flex-start;
    transform: translateX(0);
    flex-shrink: 0;
  }
`;

const LogoArea = styled.div`
  padding: 20px 18px 18px;
  border-bottom: 1px solid #F0F0F0;
  display: flex; align-items: center; gap: 12px;
`;
const LogoImg = styled.img`
  height: 36px;
  width: auto;
  max-width: 100%;
`;

const Nav = styled.nav`
  flex: 1; overflow-y: auto; padding: 8px 0;
  &::-webkit-scrollbar { width: 0; }
`;

const SectionLabel = styled.div`
  font-size: 11px; font-weight: 700; color: #C8C8C8;
  letter-spacing: 0.9px; text-transform: uppercase;
  padding: 14px 20px 5px;
`;

const SLink = styled(NavLink)`
  display: flex; align-items: center; gap: 12px;
  padding: 11px 14px; margin: 2px 10px; border-radius: 9px;
  text-decoration: none; font-size: 15px; font-weight: 600;
  color: #717171; white-space: nowrap;
  transition: background 0.14s, color 0.14s;
  svg { width: 20px; height: 20px; flex-shrink: 0; stroke-width: 1.9; }
  &:hover { background: #F7F7F7; color: #1A1A1A; }
  &.active {
    background: #FDF7F2; color: #9A563A; font-weight: 600;
    svg { stroke: #9A563A; }
  }
`;

const Bottom = styled.div`
  padding: 10px 12px;
  border-top: 1px solid #F0F0F0;
`;
const UserRow = styled.div`
  display: flex; align-items: center; gap: 10px;
  padding: 8px 10px; border-radius: 10px;
  cursor: pointer; transition: background 0.14s;
  &:hover { background: #F5F5F5; }
  &:hover .logout-icon { opacity: 1; }
`;
const Avatar = styled.div`
  width: 36px; height: 36px; border-radius: 50%;
  background: #C9A882; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  font-size: 12px; font-weight: 700; color: #fff; letter-spacing: 0.3px;
`;
const UserMeta = styled.div`
  flex: 1; min-width: 0;
  p { font-size: 13px; font-weight: 700; color: #1A1A1A; margin: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  span { font-size: 11px; color: #999; font-weight: 500; }
`;
const SignOutBtn = styled.button`
  background: none; border: none; cursor: pointer; padding: 4px;
  color: #BBBBBB; flex-shrink: 0; display: flex; align-items: center;
  border-radius: 6px; transition: color 0.14s, background 0.14s;
  svg { width: 16px; height: 16px; }
  &:hover { color: #E53935; background: #FFF0F0; }
`;

/* ── component ─────────────────────────────────────────────────────────── */
export const Sidebar: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { user, roleAccess } = useAppSelector(s => s.auth);
  const { sidebarOpen } = useAppSelector(s => s.ui);

  const visibleItems = NAV_ITEMS.filter(i => hasModuleAccess(roleAccess, ROUTE_MODULE[i.path.slice(1)] ?? '', user?.permissions));
  const mainItems  = visibleItems.filter(i => !['notifications','settings','mobile-home'].includes(i.path.slice(1)));
  const prefItems  = visibleItems.filter(i => ['notifications','settings','mobile-home'].includes(i.path.slice(1)));
  const initials   = user ? `${user.firstName?.[0]??''}${user.lastName?.[0]??''}`.toUpperCase() : 'A';

  return (
    <Wrap $open={sidebarOpen}>
      <LogoArea>
        <LogoImg src="/logo.png" alt="ZENZI Salon" />
      </LogoArea>

      <Nav>
        {mainItems.map(({ path, label, Icon }) => (
          <SLink key={path} to={path} onClick={() => dispatch(toggleSidebar())}>
            <Icon />{label}
          </SLink>
        ))}

        {prefItems.length > 0 && (
          <>
            <SectionLabel>Preferences</SectionLabel>
            {prefItems.map(({ path, label, Icon }) => (
              <SLink key={path} to={path} onClick={() => dispatch(toggleSidebar())}>
                <Icon />{label}
              </SLink>
            ))}
          </>
        )}
      </Nav>

      <Bottom>
        <UserRow onClick={() => { dispatch(logout()); navigate('/login'); }}>
          <Avatar>{initials}</Avatar>
          <UserMeta>
            <p>{user?.firstName} {user?.lastName}</p>
            <span>{user?.role?.replace(/_/g,' ') ?? 'Staff'}</span>
          </UserMeta>
          <SignOutBtn as="span" className="logout-icon" title="Sign out">
            <LogOut />
          </SignOutBtn>
        </UserRow>
      </Bottom>
    </Wrap>
  );
};

export default Sidebar;
